import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { LinhasEsqueletoTabela, ListagemBuscaField, paperListagemSx, tableContainerListagemSx } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { DialogSubgrupoDeProduto } from './DialogSubgrupoDeProduto';
import { IListagemSubGrupoProdutos, SubGrupoProdutoService } from '../../../shared/services/api/estoque/subGrupoProduto/SubGrupoProdutoService';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular } from '../../../shared/components/listagem-mobile';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const ListagemDeSubGrupoDeProdutos: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemSubGrupoProdutos[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'subgrupoprodutos';

  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 45,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  const [open, setOpen] = useState(false);
  const [detalheterceiro, setdetalheterceiro] = useState<string>('novo');
  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      SubGrupoProdutoService.getAll(pagina, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [pagina, filter, open, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const { pesquisaRef, linhaRefCallback, onBuscaKeyDown, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({ filter, pagina: novaPagina.toString() }, { replace: true }),
    onEnter: (index) => { setdetalheterceiro(rows[index].idsubgrupo); setOpen(true); },
    rowsDep: [rows],
  });

  return (
    <LayoutBaseDePagina
      titulo='Subgrupos de Produtos'
      subtitulo='Visualize, pesquise e gerencie todos os subgrupos de produtos cadastrados no sistema.'
      acao={
        <Button
          variant='contained'
          disableElevation
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => { setdetalheterceiro('novo'); setOpen(true); }}
          sx={{
            height: 44,
            borderRadius: '10px',
            px: 2.5,
            fontWeight: 600,
          }}
        >
          Adicionar
        </Button>
      }
      barraDeFerramentas={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mx: 1, mb: 2.5 }}>
          <ListagemBuscaField
            value={filter}
            onChange={(valor) => setSearchParams({ filter: valor, pagina: '1' }, { replace: true })}
            placeholder='Pesquisar por descrição do subgrupo...'
            inputRef={pesquisaRef}
            onKeyDown={onBuscaKeyDown}
          />
        </Box>
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={paperListagemSx(theme)}>
        <TableContainer
          ref={tabelaRef}
          sx={tableContainerListagemSx(containerHeight, isLoading || rows.length >= rowsPerPage)}
        >
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
            {!listaCelular && (
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>Grupo</TableCell>
                <TableCell>Subgrupo</TableCell>
              </TableRow>
            </TableHead>
            )}
            {isLoading && rows.length === 0 && (
              <TableBody>
                <LinhasEsqueletoTabela
                  colunas={[
                    { width: '50%' },
                    { width: '65%' },
                  ]}
                />
              </TableBody>
            )}
            <TableBody>
              {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idsubgrupo}
                  renderizar={(row) => (
                    <LinhaListagemMobile titulo={row.descricao} detalhes={[row.grupo]} onClick={() => { setdetalheterceiro(row.idsubgrupo); setOpen(true); }} />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map((row, index) => (
                <TableRow
                  key={row.idsubgrupo}
                  ref={linhaRefCallback(index)}
                  hover
                  tabIndex={0}
                  onClick={() => { setdetalheterceiro(row.idsubgrupo); setOpen(true); }}
                  onKeyDown={onLinhaKeyDown(index)}
                  sx={{
                    cursor: 'pointer',
                    outline: 'none',
                    '&:last-child td': { borderBottom: 0 },
                    '& td': {
                      py: 1.25,
                      px: 1.5,
                      fontSize: '0.8125rem',
                      borderBottom: `1px solid ${theme.torq.border.default}`,
                    },
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.035),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.torq.border.focus}`,
                      outlineOffset: '-2px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <TableCell>
                    <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                      {row.grupo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                      {row.descricao}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>

            {rows.length === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={2} align='center' sx={{ py: 3 }}>
                    <Box>
                      <CategoryRoundedIcon sx={{
                        fontSize: 35,
                        color: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.secondary, 0.4)
                          : 'text.secondary',
                        mb: 1
                      }} />
                      <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Adicionar" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={2} sx={{ p: 0 }}>
                    <LinearProgress sx={{ height: '1.5px' }} />
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={2} sx={{ py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
                    <Typography variant='caption' color='text.secondary'>
                      {totalCount === 0
                        ? 'Nenhum subgrupo encontrado'
                        : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} subgrupo${totalCount !== 1 ? 's' : ''}`}
                    </Typography>

                    {totalCount > rowsPerPage && (
                      <Pagination
                        page={pagina}
                        count={totalPaginas}
                        onChange={(_, newPage) => setSearchParams({ filter, pagina: newPage.toString() }, { replace: true })}
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                      />
                    )}

                    <FormControl size='small' sx={{ minWidth: 110 }}>
                      <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                        {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                          <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
      <DialogSubgrupoDeProduto
        id={detalheterceiro}
        value={''}
        open={open}
        onClose={handleClose}
      />
    </LayoutBaseDePagina>
  );
};
